import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, toISO8601 } from '@fortem-monorepo/js-utilities';
import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
export const DatePicker = ({ open, onOpenChange, selected, onSelect, buttonDisabled = false, controlsDisabled = false, placeholder = 'Pick a date', size = 'default', className, ...props }) => {
    const defaultProps = {
        mode: 'single',
        defaultMonth: selected,
        captionLayout: 'dropdown',
        endMonth: new Date('2050,12,31'),
        weekStartsOn: 1,
        selected,
        onSelect,
    };
    const openProps = useMemo(() => {
        const openprops = {};
        if (open !== undefined)
            openprops['open'] = open;
        if (onOpenChange !== undefined)
            openprops['onOpenChange'] = onOpenChange;
        return openprops;
    }, [open, onOpenChange]);
    return (_jsxs(Popover, { ...openProps, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { disabled: buttonDisabled, variant: 'outline', size: size, className: cn('justify-start text-left font-normal', {
                        ['text-muted-foreground']: !selected,
                        ['min-w-50']: true,
                    }, className), children: [_jsx(CalendarIcon, {}), selected ? format(selected, 'PPP') : _jsx("span", { children: placeholder })] }) }), _jsx(PopoverContent, { className: "w-auto p-0", children: _jsx(Calendar, { ...defaultProps, ...props }) })] }));
};
export const DatePickerWithControls = ({ todayButton, onAfterChange, ...props }) => {
    const { disabled, selected, onSelect } = props;
    const _onSelectHandler = useCallback((...args) => {
        onSelect?.(...args);
        onAfterChange?.(args[0]);
    }, [onAfterChange, onSelect]);
    /**
     * Assuming a disabled callback is defined, find the next available date
     * either going backwards of fowards, given a modifier of positive or negative 1.
     */
    const findindNextAvailableDate = (_date, callback, modifier = 1) => {
        let NUM_ATTEMPTS = 30;
        let date = null;
        let currentDate = _date;
        // Use a maximum number of tries to find a date to prevent memory leaks with infinite while loops
        // Probably never going to happen but better to be safe
        while (NUM_ATTEMPTS > 0 && !date) {
            currentDate = addDays(currentDate, modifier);
            if (!callback(currentDate)) {
                date = currentDate;
            }
            else {
                NUM_ATTEMPTS--;
            }
        }
        return date;
    };
    const isPrevDisabled = selected && disabled && typeof disabled === 'function'
        ? !findindNextAvailableDate(selected, disabled, -1)
        : undefined;
    const isNextDisabled = selected && disabled && typeof disabled === 'function'
        ? !findindNextAvailableDate(selected, disabled, 1)
        : undefined;
    return (_jsxs("div", { className: 'flex gap-2 items-center shrink-0', children: [_jsx(Button, { type: "button", size: "sm", variant: 'outline', disabled: isPrevDisabled || props.controlsDisabled || props.buttonDisabled, onClick: selected && onSelect
                    ? (e) => {
                        const prevDate = disabled && typeof disabled === 'function'
                            ? findindNextAvailableDate(selected, disabled, -1)
                            : addDays(selected, -1);
                        prevDate && _onSelectHandler(prevDate, prevDate, {}, e);
                    }
                    : undefined, children: _jsx(ChevronLeft, {}) }), _jsx(DatePicker, { ...props }), _jsx(Button, { type: "button", size: "sm", variant: 'outline', disabled: isNextDisabled || props.controlsDisabled || props.buttonDisabled, onClick: selected && onSelect
                    ? (e) => {
                        const prevDate = disabled && typeof disabled === 'function'
                            ? findindNextAvailableDate(selected, disabled, 1)
                            : addDays(selected, 1);
                        prevDate && _onSelectHandler(prevDate, prevDate, {}, e);
                    }
                    : undefined, children: _jsx(ChevronRight, {}) }), todayButton?.enabled && (_jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: (selected ? toISO8601(selected) === toISO8601(todayButton?.date ?? new Date()) : undefined) ||
                    props.buttonDisabled, onClick: (e) => {
                    e.preventDefault();
                    const _date = todayButton?.date ?? new Date();
                    onSelect && _onSelectHandler(_date, _date, {}, e);
                }, children: todayButton?.text ? todayButton.text : 'Today' }))] }));
};
export default DatePicker;
